import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, ChevronLeft, TrendingUp, AlertCircle, Receipt } from 'lucide-react';
import { useJourney } from '@/context/JourneyContext';

// Brand colors
const BRAND = {
  navy: '#001F3F',
  green: '#10B981',
  red: '#FF4136',
};

// Design system constants
const CARD_STYLES = {
  borderRadius: '18px',
  border: '1px solid #E5E7EB',
  shadow: '0 2px 8px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)',
  background: 'linear-gradient(to bottom, #FFFFFF 0%, #FAFBFC 100%)',
};

// Image URLs
const IMAGES = {
  cardA: 'https://customer-assets.emergentagent.com/job_362d2fab-7e71-445f-a778-eab4b048ca87/artifacts/f9b2sp7b_ChatGPT%20Image%20Jan%207%2C%202026%2C%2009_44_00%20AM.png',
  cardB: 'https://customer-assets.emergentagent.com/job_362d2fab-7e71-445f-a778-eab4b048ca87/artifacts/vy7thh6s_ChatGPT%20Image%20Jan%207%2C%202026%2C%2009_40_06%20AM.png',
  cardC: 'https://customer-assets.emergentagent.com/job_362d2fab-7e71-445f-a778-eab4b048ca87/artifacts/l2btbywx_ChatGPT%20Image%20Jan%207%2C%202026%2C%2010_15_12%20AM.png',
};

const JourneyIntro = ({ isEmbedded = false, onContinue: propOnContinue, onBack: propOnBack }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { startPath } = useJourney();
  const selectedPath = searchParams.get('path');

  const handleBack = () => {
    if (isEmbedded && propOnBack) {
      propOnBack();
    } else {
      navigate('/journey');
    }
  };

  const handleContinue = () => {
    if (isEmbedded && propOnContinue) {
      propOnContinue();
    } else if (selectedPath) {
      startPath(selectedPath);
      navigate(`/journey/${selectedPath}`);
    } else {
      navigate('/journey');
    }
  };

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: '#F8F9FA' }}>
      {/* Content */}
      <div className="max-w-screen-sm mx-auto px-4">
        
        {/* Back Button + Brand - matching Car buying journey header */}
        <div className="pt-4 pb-3 flex items-center justify-between">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 hover:opacity-70 transition-opacity"
            style={{ color: BRAND.navy }}
            data-testid="journey-intro-back-btn"
          >
            <ChevronLeft className="w-5 h-5" strokeWidth={2} />
            <span className="font-medium">Back</span>
          </button>
          <span className="text-sm font-semibold" style={{ color: BRAND.navy }}>
            VINdicated.<span style={{ color: BRAND.red }}>ai</span>
          </span>
        </div>

        {/* Cards Container */}
        <div className="space-y-5">

        {/* NEW INTRO BLOCK - Added above the existing content */}
        <div 
          style={{
            background: '#FFFFFF',
            borderRadius: '18px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)',
            padding: '20px',
          }}
        >
          <p className="text-sm leading-relaxed mb-3" style={{ color: '#374151', lineHeight: 1.7 }}>
            Dealers use repeatable scripts to rush decisions, steer the conversation, and protect their profit.
          </p>
          <p className="text-sm leading-relaxed" style={{ color: '#374151', lineHeight: 1.7 }}>
            We'll show you what you may hear, why it works, and how to respond - so you don't pay more because they rushed the decision.
          </p>
        </div>
        
        {/* CARD A - Leverage Section */}
        <div 
          style={{
            background: '#FFFFFF',
            borderRadius: '18px',
            border: 'none',
            outline: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div className="p-5" style={{ paddingTop: '20px' }}>
            {/* Badge + Headline Row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div 
                style={{ 
                  width: '36px', 
                  height: '36px', 
                  borderRadius: '50%', 
                  backgroundColor: 'rgba(0, 31, 63, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <TrendingUp className="w-5 h-5" style={{ color: BRAND.navy }} strokeWidth={2} />
              </div>
              <h2 className="text-xl font-bold" style={{ color: BRAND.navy, margin: 0 }}>
                Most of your car buying leverage happens before you step into a dealership
              </h2>
            </div>
            
            <p className="text-sm leading-relaxed mb-4" style={{ color: '#374151', lineHeight: 1.7 }}>
              Do the work from home first. The dealership should be one of the last steps.
            </p>
            <p className="text-sm leading-relaxed mb-4" style={{ color: '#374151', lineHeight: 1.7 }}>
              Budget limits, price comparisons, trade-in values, and financing options are best decided ahead of time - when you can think clearly, compare offers, and avoid pressure.
            </p>
            <p className="text-sm leading-relaxed mb-4" style={{ color: '#374151', lineHeight: 1.7 }}>
              This is why VINdicated focuses on preparation first.
            </p>
            <p className="text-sm leading-relaxed" style={{ color: '#374151', lineHeight: 1.7 }}>
              A prepared buyer is harder to rush, harder to confuse, and harder to overcharge.
            </p>
          </div>
        </div>

        {/* CARD B - Loss Aversion Section */}
        <div 
          style={{
            background: '#FFFFFF',
            borderRadius: '18px',
            border: 'none',
            outline: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div className="p-5" style={{ paddingTop: '20px' }}>
            {/* Badge + Headline Row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div 
                style={{ 
                  width: '36px', 
                  height: '36px', 
                  borderRadius: '50%', 
                  backgroundColor: 'rgba(0, 31, 63, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <AlertCircle className="w-5 h-5" style={{ color: BRAND.navy }} strokeWidth={2} />
              </div>
              <h2 className="text-lg font-bold" style={{ color: BRAND.navy, margin: 0 }}>
                You may hear this - and it&apos;s not a coincidence:
              </h2>
            </div>
            
            <p className="text-sm leading-relaxed italic mb-2" style={{ color: '#6B7280' }}>
              &ldquo;Someone else called about this today.&rdquo;
            </p>
            <p className="text-sm leading-relaxed italic mb-2" style={{ color: '#6B7280' }}>
              &ldquo;Another buyer is coming in later.&rdquo;
            </p>
            <p className="text-sm leading-relaxed italic mb-4" style={{ color: '#6B7280' }}>
              &ldquo;This won&apos;t last long.&rdquo;
            </p>
            
            <p className="text-sm leading-relaxed mb-4" style={{ color: '#374151', lineHeight: 1.7 }}>
              This is a well-known psychological effect called loss aversion. People feel the pain of losing something more strongly than the benefit of gaining it.
            </p>
            
            <p className="text-sm leading-relaxed mb-4" style={{ color: '#374151', lineHeight: 1.7 }}>
              Imagine you find an old sweatshirt in your closet that you haven&apos;t worn in years. You&apos;re about to donate it… then your brain says, &ldquo;Wait, I might wear this someday.&rdquo;
            </p>
            <p className="text-sm leading-relaxed mb-4" style={{ color: '#374151', lineHeight: 1.7 }}>
              But if you saw that same sweatshirt at a garage sale for $3, you probably wouldn&apos;t buy it.
            </p>
            
            <p className="text-sm leading-relaxed mb-4" style={{ color: '#374151', lineHeight: 1.7 }}>
              That&apos;s loss aversion. Once something feels &ldquo;yours,&rdquo; letting it go feels worse than it should.
            </p>
            
            <p className="text-sm leading-relaxed mb-4" style={{ color: '#374151', lineHeight: 1.7 }}>
              Dealers use urgency to trigger that feeling, pushing decisions before you&apos;ve secured financing, compared totals, verified the math, or reviewed other options.
            </p>
            
            <p className="text-sm leading-relaxed" style={{ color: '#374151', lineHeight: 1.7 }}>
              Urgency is not proof of a good deal. Even if a car feels perfect, there&apos;s almost always another similar one nearby or arriving soon.
            </p>
          </div>
        </div>

        {/* CARD C - New Car Feeling Section */}
        <div 
          style={{
            background: '#FFFFFF',
            borderRadius: '18px',
            border: 'none',
            outline: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div className="p-5" style={{ paddingTop: '20px' }}>
            {/* Badge + Headline Row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div 
                style={{ 
                  width: '36px', 
                  height: '36px', 
                  borderRadius: '50%', 
                  backgroundColor: 'rgba(0, 31, 63, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Receipt className="w-5 h-5" style={{ color: BRAND.navy }} strokeWidth={2} />
              </div>
              <h2 className="text-lg font-bold" style={{ color: BRAND.navy, margin: 0 }}>
                New car feeling fades. The payment doesn&apos;t.
              </h2>
            </div>
            
            <p className="text-sm leading-relaxed mb-4" style={{ color: '#374151', lineHeight: 1.7 }}>
              The perfect car feeling is powerful, and temporary.
            </p>
            <p className="text-sm leading-relaxed mb-4" style={{ color: '#374151', lineHeight: 1.7 }}>
              Most big purchases you made years ago probably don&apos;t feel magical anymore. They&apos;re just stuff.
            </p>
            <p className="text-sm leading-relaxed mb-4" style={{ color: '#374151', lineHeight: 1.7 }}>
              Slow down long enough to compare totals, verify the math, and walk away if anything feels off.
            </p>
            <p className="text-sm leading-relaxed" style={{ color: '#374151', lineHeight: 1.7 }}>
              A car is a tool. The goal is a deal you won&apos;t be second-guessing six months from now.
            </p>
          </div>
        </div>

        {/* CTA Button */}
        <div className="pt-4">
          <button
            onClick={handleContinue}
            className="w-full py-4 font-semibold text-base rounded-xl transition-all flex items-center justify-center gap-2 hover:opacity-90 active:scale-[0.99]"
            style={{ 
              backgroundColor: BRAND.navy, 
              color: '#FFFFFF',
              boxShadow: '0 4px 12px rgba(0,31,63,0.20)',
            }}
            data-testid="journey-intro-continue-btn"
          >
            <span>Continue to choose your path</span>
            <ArrowRight className="w-4 h-4" style={{ color: BRAND.red }} strokeWidth={2.5} />
          </button>
        </div>
        </div>
      </div>
    </div>
  );
};

export default JourneyIntro;
